"use client";

import React, { useCallback, useEffect, useState } from "react";
import { learningApi } from "@/lib/api/services/learning.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, BookOpen, Clock, Users, ArrowRight, Sparkles, Trophy, Play, CheckCircle2, Star } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import Link from "next/link";
import { toast } from "@/components/ui/toast";
import { Pagination } from "@/components/shared/pagination";
import { trackEvent } from "@/hooks/use-analytics";

interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  instructor: string;
  duration: string;
  lessons: number;
  enrolledCount: number;
  tags: string[];
  progress?: number;
  certificate?: boolean;
  level: string;
}

export default function LearningCoursesPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const perPage = 12;

  const fetchCourses = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await learningApi.listCourses({
        page,
        perPage,
        sort: "enrolledCount",
        order: "desc",
      });
      const payload = data as any;
      setCourses(payload.data ?? []);
      setTotal(payload.meta?.total ?? payload.total ?? (payload.data?.length ?? 0));
      setTotalPages(
        payload.meta?.totalPages ??
          payload.totalPages ??
          Math.max(1, Math.ceil((payload.meta?.total ?? 0) / perPage) || 1)
      );
    } catch {
      setCourses([]);
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  const handleEnroll = async (courseId: string) => {
    try {
      await learningApi.enroll(courseId);
      trackEvent("enroll", { entity: "course", id: courseId });
      toast({ type: "success", title: "Enrolled successfully", message: "You have been enrolled in this course." });
    } catch {
      toast({ type: "error", title: "Error", message: "Failed to enroll. Please try again." });
    }
  };

  const filtered = filter === "all" ? courses : courses.filter((c) => c.category === filter);
  const categories = [...new Set(courses.map((c) => c.category))];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Learning</span>
            </div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground">All Courses</h1>
            <p className="mt-1 text-sm text-muted-foreground">Browse and enroll in professional development courses.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground" aria-label="Filter by category">
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-5">
                  <div className="h-32 bg-muted/10 rounded-xl animate-pulse mb-4" />
                  <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                  <div className="mt-2 h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  <div className="mt-4 h-2 w-full bg-muted/10 rounded-full" />
                </CardContent>
              </Card>
            ))}
          </div>
         ) : filtered.length === 0 ? (
           <EmptyState
             variant="guide"
             icon={BookOpen}
             title="No courses found"
             description="Try adjusting your filter or check back later."
           />
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((course) => (
                <Card key={course.id} className="group hover:border-neutral-300 transition-all duration-150 hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="relative mb-4">
                      <div className="aspect-[16/9] bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl flex items-center justify-center">
                        <BookOpen className="h-8 w-8 text-primary/30" />
                      </div>
                      {course.progress !== undefined && course.progress > 0 && (
                        <div className="absolute bottom-2 left-2 right-2">
                          <Progress value={course.progress} className="h-1.5" />
                          <p className="mt-1 text-[0.625rem] text-primary font-medium text-right">{course.progress}%</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="h-4 px-1.5 text-[0.625rem] font-medium capitalize">{course.category}</Badge>
                      <Badge variant="outline" className="h-4 px-1.5 text-[0.625rem] font-medium capitalize">{course.level}</Badge>
                    </div>
                    <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                      {course.title}
                    </h3>
                    <p className="text-xs text-muted-leading-relaxed line-clamp-2 mb-3">{course.description}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {course.duration}</span>
                      <span className="text-neutral-300">·</span>
                      <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" /> {course.lessons} lessons</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> {course.enrolledCount}</span>
                      {course.certificate && (
                        <Trophy className="h-3.5 w-3.5 text-amber-500" />
                      )}
                    </div>
                    <Button
                      variant="premium"
                      size="sm"
                      className="mt-4 w-full"
                      onClick={() => handleEnroll(course.id)}
                    >
                      {course.progress !== undefined && course.progress > 0 ? "Continue" : "Enroll Now"}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8">
              <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} perPage={perPage} />
            </div>
          </>
        )}

        {!isLoading && filtered.length > 0 && (
          <div className="mt-12 space-y-6">
            <div className="grid gap-6 md:grid-cols-3">
              <Card className="hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Trophy className="h-6 w-6" /></div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Certificates</p>
                      <p className="text-xl font-bold text-foreground tabular-nums">Earn & Share</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Complete courses to earn verifiable certificates that showcase your expertise.</p>
                  <Button variant="ghost" size="sm" className="mt-4 p-0 h-auto text-xs gap-1" asChild>
                    <Link href="/certificates">View Certificates <ArrowRight className="ml-1 h-3 w-3" /></Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-600"><Star className="h-6 w-6" /></div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">AI Tutor</p>
                      <p className="text-xl font-bold text-foreground tabular-nums">Guided Learning</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Get personalized guidance and recommendations from our AI learning assistant.</p>
                  <Button variant="ghost" size="sm" className="mt-4 p-0 h-auto text-xs gap-1" asChild>
                    <Link href="/ai">Try AI Tutor <ArrowRight className="ml-1 h-3 w-3" /></Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100 text-green-600"><Users className="h-6 w-6" /></div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Peer Learning</p>
                      <p className="text-xl font-bold text-foreground tabular-nums">Discussions</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Connect with peers, share insights, and discuss course topics in dedicated forums.</p>
                  <Button variant="ghost" size="sm" className="mt-4 p-0 h-auto text-xs gap-1" asChild>
                    <Link href="/discussions">Join Discussions <ArrowRight className="ml-1 h-3 w-3" /></Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}